/**
 * 63.14 step 2: a building drawn from the kit (orbital-design-3's rendering/baseKit.ts, 67.65) by its layout
 * (core/modules.ts) — every module an instance of its kind-and-form prototype, one instanced mesh per prototype the
 * building uses (BaseKitField), in the structure's own group where today's baked model stands, at the match's person.
 * The damaged form shows a keyed share of its walls and parapets damaged (core/modules.ts damagedModules); 72.1: the ruin
 * its walls' heaps (buildLayoutRuin). Presentation only: the sim reads the building's one box (63.9) whatever is drawn.
 */
import * as THREE from 'three';
import { BaseKitField, U, GATE_LEAVES, moduleGeometry, MASONRY_KINDS, masonryRoofOf, type ModulePlacement, type GateKind, type BaseModule, type MasonryLook } from './baseKit';
import type { UnitPalette } from './unitPalette';
import type { BuildingLayout, ModuleForm } from '../types/modules';
import type { StructureState, PlanetState, PlanetArchetype } from '../types';
import { posesOf, damagedModules, layoutExtent } from '../core/modules';
import { shutOf, layoutRuin } from '../core/moduleShapes';
import { MODULE_BODY, DRAWN_BY_THE_POOL, RUBBLE, MODULE_CELL, MODULE_SLAB, COLLAPSE } from '../constants/modules';
import { footOf } from '../core/collapse';

/** What a building's look costs: its draws (one an instanced mesh), its instances and triangles. */
export interface LayoutLookCost { draws: number; triangles: number; instances: number }

/**
 * 72.5 (DECISIONS 325): each archetype's two masonry looks, a settlement taking one (`settlementLook`) — render and adobe on the
 * arid and bleached planets, fieldstone and brick on the temperate and green ones.
 */
export const SETTLEMENT_LOOKS: Record<PlanetArchetype, readonly [MasonryLook, MasonryLook]> = {
  thalia: ['fieldstone', 'brick'], bruma: ['fieldstone', 'brick'], viridis: ['fieldstone', 'brick'], tephra: ['fieldstone', 'render'],
  murex: ['brick', 'render'], pallor: ['render', 'adobe'], pelago: ['render', 'fieldstone'], ochrus: ['adobe', 'render'], sonora: ['adobe', 'fieldstone'],
};
const hash01 = (a: number, b: number): number => (Math.imul((Math.imul(a + 0x9e3779b9, 2654435761) ^ Math.imul(b + 1, 40503)) >>> 0, 2246822519) >>> 0) / 4294967296;
/**
 * A settlement's look: one of its planet archetype's two, keyed by the planet and the settlement's lane (every lot of a
 * settlement turns as its lane or half round from it, core/settlements.ts, so its yaw half a turn round keys it).
 */
export function settlementLook(planet: Pick<PlanetState, 'id' | 'archetype'>, yaw: number): MasonryLook {
  const lane = Math.round((((yaw % Math.PI) + Math.PI) % Math.PI) * 1e4) % 31416;
  const pair = SETTLEMENT_LOOKS[planet.archetype] ?? SETTLEMENT_LOOKS.thalia;
  return pair[hash01(planet.id, lane) < 0.5 ? 0 : 1];
}
/** A look's share of doors drawn arched (adobe's all). */
const ARCHED: Record<MasonryLook, number> = { render: 0.25, fieldstone: 0.5, brick: 0.25, adobe: 1 };

/**
 * The masonry placements' turns and variants by module index (72.5): a door arched by its key; a window's or a slit's frame
 * colour by the house's; a roof cell flat (`f{mask}`, its open edges: no roof beside it and no storey on the cell beside it) or,
 * in a pitched look where nothing stands over the roof level's extent, pitched (`p{n}.{i}.{mask}`, the ridge along the level's
 * longer side, the cell `i` of `n` across it, turned a quarter where the ridge runs along z). See baseKit.ts buildMasonry.
 */
function masonryVariants(layout: BuildingLayout, look: MasonryLook, seed: number): Map<number, { variant: string; yaw?: number }> {
  const out = new Map<number, { variant: string; yaw?: number }>(), poses = posesOf(layout), cell = MODULE_CELL;
  const at = (kind: string, x: number, y: number, z: number) => `${kind}|${Math.round(x * 100)}|${Math.round(y * 100)}|${Math.round(z * 100)}`;
  const there = new Set(poses.map(p => at(p.kind, p.x, p.y, p.z)));
  const frame = `c${Math.floor(hash01(seed, 7) * 6)}`;
  const levels = new Map<number, Array<typeof poses[number]>>();
  for (const p of poses) {
    if (p.kind === 'wallWindow' || p.kind === 'wallSlit') out.set(p.index, { variant: frame });
    else if (p.kind === 'wallDoor' && hash01(seed, p.index + 100) < ARCHED[look]) out.set(p.index, { variant: 'arch' });
    else if (p.kind === 'roof') { if (!levels.has(p.y)) levels.set(p.y, []); levels.get(p.y)!.push(p); }
  }
  for (const [y, cells] of levels) {
    const xs = cells.map(c => c.x), zs = cells.map(c => c.z), x0 = Math.min(...xs), x1 = Math.max(...xs), z0 = Math.min(...zs), z1 = Math.max(...zs);
    const covered = poses.some(p => p.kind === 'floor' && Math.abs(p.y - (y + cell)) < 0.05 && p.x > x0 - cell / 2 && p.x < x1 + cell / 2 && p.z > z0 - cell / 2 && p.z < z1 + cell / 2);
    const pitched = masonryRoofOf(look) === 'pitched' && !covered, ridgeAlongX = x1 - x0 >= z1 - z0;
    for (const c of cells) {
      const open = (dx: number, dz: number) => !there.has(at('roof', c.x + dx, y, c.z + dz)) && !there.has(at('floor', c.x + dx, y + cell, c.z + dz));
      const world = [open(0, -cell), open(0, cell), open(-cell, 0), open(cell, 0)];   // −z, +z, −x, +x
      if (!pitched) { out.set(c.index, { variant: `f${world.reduce((m, o, b) => m | (o ? 1 << b : 0), 0)}`, yaw: 0 }); continue; }
      // the cell's frame: the ridge along its x (a quarter turn where the ridge runs along z: its −z the world's −x, its −x the world's +z)
      const local = ridgeAlongX ? world : [world[2], world[3], world[1], world[0]];
      const n = Math.round((ridgeAlongX ? z1 - z0 : x1 - x0) / cell) + 1, i = Math.round(((ridgeAlongX ? c.z - z0 : c.x - x0)) / cell);
      out.set(c.index, { variant: `p${n}.${i}.${local.reduce((m, o, b) => m | (o ? 1 << b : 0), 0)}`, yaw: ridgeAlongX ? 0 : Math.PI / 2 });
    }
  }
  return out;
}
/** A pose's placement in a look (the masonry kinds), else the team kit's. */
function inLook(q: ModulePlacement, index: number, look: MasonryLook | undefined, variants: Map<number, { variant: string; yaw?: number }> | null): ModulePlacement {
  if (!look || !MASONRY_KINDS.has(q.kind)) return q;
  const v = variants?.get(index);
  return { ...q, look, ...(v ? { variant: v.variant, ...(v.yaw !== undefined ? { yaw: v.yaw } : {}) } : {}) };
}

/**
 * A layout as the kit draws it, `personHeight` world units a person (the kit models a person as U = 1.8): the group to
 * stand in the structure's place, and its cost. `form` damaged: the keyed share of modules in their damaged form. `look`: its
 * walls, piers and roofs in masonry (72.5: a settlement's house, `settlementLook`).
 */
export function buildLayoutLook(layout: BuildingLayout, palette: UnitPalette, personHeight: number, form: 'whole' | 'damaged' = 'whole', seed = 1, worn?: Pick<StructureState, 'modules' | 'rubble'> & Partial<Pick<StructureState, 'shut'>>, look?: MasonryLook): { group: THREE.Group; field: BaseKitField; cost: LayoutLookCost } {
  // 63.14 step 4: a module's own health picks its form — destroyed (its rubble, while it lies), damaged under half, whole;
  // a destroyed module whose rubble is gone is not drawn. Without module healths, the keyed share for the damaged form.
  const hurt = form === 'damaged' && !worn?.modules ? damagedModules(layout, seed) : null;
  const heaps = new Map((worn?.rubble ?? []).map(r => [r.k, r]));
  const formOf = (index: number): ModuleForm | null => {
    const h = worn?.modules?.[index];
    if (h === undefined) return hurt?.has(index) ? 'damaged' : 'whole';
    if (h <= 0) return heaps.has(index) ? 'destroyed' : null;
    return h <= 0.5 * MODULE_BODY[layout.modules[index].kind].health ? 'damaged' : 'whole';
  };
  // 63.16 (b2): a gate's leaves posed by the sim's gate state (core/moduleShapes.ts shutOf: the structure's `shut` list, else the default) — shut across the opening, open swung into the yard
  const shut = shutOf(worn ?? {}, layout);
  const placements: ModulePlacement[] = [], variants = look ? masonryVariants(layout, look, seed) : null;
  for (const p of posesOf(layout)) {
    if (DRAWN_BY_THE_POOL.has(p.kind)) continue;
    const f = formOf(p.index);
    if (!f) continue;
    // 72.3 step 3: a fallen module's heap where it landed (its `y`); a slab's a deck stretched to the sim's heap
    const heap = f === 'destroyed' ? heaps.get(p.index) : undefined;
    if (heap && COLLAPSE.heapKinds.has(p.kind)) { placements.push({ kind: 'floor', form: 'destroyed', x: p.x * U, y: (heap.y ?? p.y) * U, z: p.z * U, yaw: p.yaw, stretch: RUBBLE.heightPeople / MODULE_SLAB }); continue; }
    placements.push(inLook({ kind: p.kind as BaseModule, form: f, x: p.x * U, y: (heap?.y ?? p.y) * U, z: p.z * U, yaw: p.yaw, ...(GATE_LEAVES[p.kind as GateKind] ? { open: shut.has(p.index) ? 0 : 1 } : {}) }, p.index, look, variants));
  }
  const field = new BaseKitField(palette, placements, personHeight / U);
  field.group.name = `layout:${layout.key}:${form}`;
  return { group: field.group, field, cost: field.cost() };
}

const destroyedTops = new Map<BaseModule, number>();
/** A kind's destroyed form's top (model units). */
function destroyedTop(kind: BaseModule, palette: UnitPalette): number {
  let top = destroyedTops.get(kind);
  if (top === undefined) {
    const g = moduleGeometry(kind, palette, 'destroyed');
    g.computeBoundingBox();
    destroyedTops.set(kind, top = g.boundingBox!.max.y);
    g.dispose();
  }
  return top;
}

/** A collapse's falling modules drawn (72.3 step 3): `update(tick)` poses each down its column for the sim's tick. */
export interface FallingLook { group: THREE.Group; update(tick: number): void }

/**
 * 72.3 step 3 (ARCHITECTURE §5.35 "The drawing": a falling module is presentation): the modules of a collapse falling, each whole
 * from its pose down its column to where the sim lands it (StructureState.collapse.falls: from, lands, y), under constant
 * acceleration, tumbling a keyed little about its across; gone on its landing tick, when its heap is drawn (the rubble). Instanced
 * by prototype (masonry too: `merge` off), so a fall of 60 modules is a dozen draws for the second it lasts.
 */
export function buildFallingLook(layout: BuildingLayout, palette: UnitPalette, personHeight: number, falls: NonNullable<StructureState['collapse']>['falls'], seed = 1, look?: MasonryLook): FallingLook {
  const poses = posesOf(layout), variants = look ? masonryVariants(layout, look, seed) : null;
  const placements = falls.map(f => { const p = poses[f.k]; return inLook({ kind: p.kind as BaseModule, form: 'whole', x: p.x * U, y: p.y * U, z: p.z * U, yaw: p.yaw }, f.k, look, variants); });
  const field = new BaseKitField(palette, placements, personHeight / U, false);
  field.group.name = `layout:${layout.key}:falling`;
  for (const m of field.meshes.values()) m.frustumCulled = false;   // (they move: the bound is the pose's)
  const m4 = new THREE.Matrix4(), at = new THREE.Vector3(), q = new THREE.Quaternion(), tumble = new THREE.Quaternion(), one = new THREE.Vector3(1, 1, 1), none = new THREE.Vector3(0, 0, 0);
  const drops = falls.map(f => (footOf(layout, f.k) - f.y) * U);
  const spins = falls.map(f => ((((f.k + 1) * 2654435761 + seed * 40503) >>> 0) / 4294967296 - 0.5) * 0.9);   // (keyed: ±0.45 rad by its landing)
  const AX = new THREE.Vector3(1, 0, 0), UP = new THREE.Vector3(0, 1, 0);
  return {
    group: field.group,
    update(tick: number): void {
      falls.forEach((f, n) => {
        const u = Math.min(1, Math.max(0, (tick - f.from) / Math.max(1, f.lands - f.from))), pl = placements[n];
        q.setFromAxisAngle(UP, pl.yaw ?? 0).multiply(tumble.setFromAxisAngle(AX, spins[n] * u));
        at.set(pl.x, (pl.y ?? 0) - drops[n] * u * u, pl.z);
        field.setMatrix(n, m4.compose(at, q, tick >= f.lands ? none : one));
      });
    },
  };
}

/**
 * 72.1: a modular building's ruin (the structure at 0) as the sim stands it (core/moduleShapes.ts layoutRuin): each ground
 * floor wall's heap, the kit's destroyed form squashed to the sim's rubble height (RUBBLE.heightPeople), so the ruin a
 * person steps over and sees across is drawn so. It was the builder's old broken model. (63.14: the kit's destroyed forms
 * now stand at the heap themselves, rendering/baseKit.ts downToHeap, so the squash is about 1, and a module destroyed in a
 * standing building is drawn at the heap's height too.)
 */
export function buildLayoutRuin(layout: BuildingLayout, palette: UnitPalette, personHeight: number, look?: MasonryLook): { group: THREE.Group; field: BaseKitField; cost: LayoutLookCost } {
  const heaps = new Set(layoutRuin(layout).rubble.map(r => r.k));
  const placements: ModulePlacement[] = [];
  for (const p of posesOf(layout)) {
    if (!heaps.has(p.index) || DRAWN_BY_THE_POOL.has(p.kind)) continue;
    const kind = p.kind as BaseModule, q: ModulePlacement = { kind, form: 'destroyed', x: p.x * U, y: p.y * U, z: p.z * U, yaw: p.yaw };
    placements.push(look && MASONRY_KINDS.has(kind) ? { ...q, look } : { ...q, stretch: (RUBBLE.heightPeople * U) / destroyedTop(kind, palette) });   // (72.5: masonry's heaps stand under the sim's already)
  }
  const field = new BaseKitField(palette, placements, personHeight / U);
  field.group.name = `layout:${layout.key}:ruin`;
  return { group: field.group, field, cost: field.cost() };
}

/** The look's footprint for its plinth (world units): the layout's extent at `personHeight` a person. */
export function layoutLookExtent(layout: BuildingLayout, personHeight: number): { half: number; height: number } {
  const e = layoutExtent(layout);
  return { half: Math.max(e.halfX, e.halfZ) * personHeight, height: e.height * personHeight };
}
